import { useEffect, useState, useSyncExternalStore } from 'react';
import { NavLink, Navigate, Route, Routes, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { MONTH_NAMES } from '@pk/shared';
import { getToken, onTokenChange } from './api/token';
import { epochMsOf } from './lib/eta';
import { memberIds, useChatChannels, useHouse, useMe, useWorld } from './api/hooks';
import { useGameSocket, useHeartbeat } from './api/ws';
import { registerPush } from './lib/push';
import { useUi } from './store';
import { Loading, ErrorBox, Sheet } from './components/ui';
import { Countdown, GameHour, ResourceChips, formatAmount, formatBalance } from './components/game';
import { IconBook, IconChat, IconChevron, IconCrown, IconGear, IconHourglass, IconHouse, IconMap, IconSearch, ResGold, ResourceIcon, ResPrestige } from './components/icons';
import { AuthScreen } from './screens/Auth';
import { EndedScreen, FoundHouseScreen } from './screens/FoundHouse';
import { HouseScreen } from './screens/House';
import { CharacterScreen } from './screens/Character';
import { TitleScreen } from './screens/Title';
import { MapScreen } from './screens/Map';
import { CourtScreen } from './screens/Court';
import { ChatScreen } from './screens/Chat';
import { ChronicleScreen } from './screens/Chronicle';
import { TreeScreen } from './screens/Tree';
import { SearchScreen } from './screens/Search';
import { SettingsScreen } from './screens/Settings';
import { TradeScreen } from './screens/Trade';

function useToken(): string | null {
  return useSyncExternalStore(onTokenChange, getToken, getToken);
}

export function App() {
  const token = useToken();
  const qc = useQueryClient();
  useEffect(() => { if (!token) qc.clear(); }, [token, qc]);
  return (
    <>
      {token ? <Authed /> : <AuthScreen />}
      <Toasts />
    </>
  );
}

function Authed() {
  const me = useMe();
  const endedDismissed = useUi((s) => s.endedDismissed);
  if (me.isLoading) return <div className="splash-page"><div className="splash"><Loading /></div></div>;
  if (me.isError) return <div className="splash-page"><div className="splash"><ErrorBox error={me.error} onRetry={() => me.refetch()} /></div></div>;
  const m = me.data!;
  if (m.lastHouseEnded && !endedDismissed) return <EndedScreen ended={m.lastHouseEnded} />;
  if (!m.houseId) return <FoundHouseScreen />;
  return <Shell />;
}

/** Tracks the on-screen keyboard (visualViewport shrinks): sets `kb-open` on <html> and `--vvh` (visible height). */
function useKeyboardInset() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    const update = () => {
      const open = window.innerHeight - vv.height > 140;
      root.classList.toggle('kb-open', open);
      root.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
      if (open) window.scrollTo(0, 0);
    };
    update();
    vv.addEventListener('resize', update);
    return () => { vv.removeEventListener('resize', update); root.classList.remove('kb-open'); root.style.removeProperty('--vvh'); };
  }, []);
}

function Shell() {
  const { t } = useTranslation();
  const loc = useLocation();
  useGameSocket();
  useHeartbeat(true);
  useKeyboardInset();
  useEffect(() => { void registerPush({ requestPermission: false }); }, []);
  const house = useHouse();
  const world = useWorld();
  const mine = memberIds(house.data);
  const unread = (house.data?.letters ?? []).filter((l) => !l.read && mine.has(l.toId) && l.arriveDay <= (world.data?.day ?? 0)).length
    + (house.data?.incomingProposals ?? []).filter((p) => p.status === 'pending').length;
  const chat = useChatChannels(true); // auth-gated: the shell only renders when logged in
  const chatUnread = (chat.data?.channels ?? []).reduce((n, c) => n + c.unread, 0);
  const isMap = loc.pathname === '/map' || loc.pathname.startsWith('/tree/');

  const tabs = [
    { to: '/', label: t('nav.house'), icon: <IconHouse />, end: true },
    { to: '/map', label: t('nav.map'), icon: <IconMap />, end: true },
    { to: '/trade', label: t('nav.economy'), icon: <ResGold /> },
    { to: '/court', label: t('nav.court'), icon: <IconCrown />, badge: unread },
    { to: '/chat', label: t('nav.chat'), icon: <IconChat />, badge: chatUnread },
  ];

  return (
    <div className="shell">
      <Hud />
      <main className={`main ${isMap ? 'wide' : ''}`}>
        <Routes>
          <Route path="/" element={<HouseScreen />} />
          <Route path="/character/:id" element={<CharacterScreen />} />
          <Route path="/tree/:houseId" element={<TreeScreen />} />
          <Route path="/search" element={<SearchScreen />} />
          <Route path="/realm" element={<Navigate to="/map" replace />} />
          <Route path="/title/:id" element={<TitleScreen />} />
          <Route path="/map" element={<MapScreen />} />
          <Route path="/military" element={<Navigate to="/map?view=military" replace />} />
          <Route path="/family" element={<Navigate to="/?view=family" replace />} />
          <Route path="/court" element={<CourtScreen />} />
          <Route path="/chat" element={<ChatScreen />} />
          <Route path="/trade" element={<TradeScreen />} />
          <Route path="/letters" element={<Navigate to="/court" replace />} />
          <Route path="/chronicle" element={<ChronicleScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <nav className="tabbar" aria-label={t('nav.label')}>
        <Link to="/" className="side-logo" aria-label={t('app.name')}><img src="/brand/logo-full.png" alt="" width={160} height={160} /></Link>
        {tabs.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}>
            <span className="tab-ico">
              {tab.icon}
              {tab.badge ? <span className="dot num" aria-hidden="true">{tab.badge}</span> : null}
            </span>
            <span className="tab-label">{tab.label}</span>
            {tab.badge ? <span className="dot side num" aria-label={t('nav.unread', { count: tab.badge })}>{tab.badge}</span> : null}
          </NavLink>
        ))}
        <div className="nav-extra">
          <NavLink to="/chronicle" className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}><span className="tab-ico"><IconBook /></span><span className="tab-label">{t('nav.chronicle')}</span></NavLink>
          <NavLink to="/search" className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}><span className="tab-ico"><IconSearch /></span><span className="tab-label">{t('nav.search')}</span></NavLink>
          <NavLink to="/settings" className={({ isActive }) => `tab ${isActive ? 'active' : ''}`}><span className="tab-ico"><IconGear /></span><span className="tab-label">{t('nav.settings')}</span></NavLink>
        </div>
      </nav>
    </div>
  );
}

/** Sticky top bar: brand, date + countdown, key resources, quick links, connection dot. */
function Hud() {
  const { t } = useTranslation();
  const house = useHouse();
  const world = useWorld();
  const wsConnected = useUi((s) => s.wsConnected);
  const [open, setOpen] = useState(false);
  const w = world.data;
  const h = house.data;
  const chips: { k: 'gold' | 'food' | 'manpower'; }[] = [{ k: 'gold' }, { k: 'food' }, { k: 'manpower' }];
  const link = ({ isActive }: { isActive: boolean }) => `hud-btn ${isActive ? 'active' : ''}`;
  return (
    <header className="hud">
      <Link to="/" className="hud-brand" aria-label={t('app.name')}>
        <img className="hud-icon" src="/brand/logo-icon-128.png" alt="" width={38} height={38} />
        <img className="hud-wordmark" src="/brand/logo-text.png" alt={t('app.name')} />
      </Link>
      <div className="hud-date">
        {w ? (
          <>
            <span className="hud-day num">{w.date.day} {MONTH_NAMES[w.date.month - 1]?.slice(0, 3)} {w.date.year}</span>
            <span className="hud-sub">
              <span className={`season-pip season-${w.date.season}`} aria-hidden="true" />
              {t(`season.${w.date.season}`)}
              <GameHour epochMs={epochMsOf(w)} />
              <span className="hud-cd">
                <IconHourglass />
                <span className="sr-only">{t('house.nextDay')}</span>
                <Countdown key={w.nextDayAtMs} targetMs={w.nextDayAtMs} />
              </span>
            </span>
          </>
        ) : null}
      </div>
      <div className="hud-res">
        {h && chips.map(({ k }, i) => {
          const amount = h.house.resources[k];
          const b = h.monthlyBalance[k];
          const trend = b > 0.05 ? 'pos' : b < -0.05 ? 'neg' : '';
          return (
            <button key={k} type="button" className={`hud-chip ${i > 0 ? 'extra' : ''}`} onClick={() => setOpen(true)} aria-haspopup="dialog"
              aria-label={`${t(`resource.${k}`)}: ${Math.floor(amount)} (${formatBalance(b)} ${t('house.perMonth')}). ${t('ui.resources')}`} title={`${t(`resource.${k}`)} ${formatBalance(b)} ${t('house.perMonthShort')}`}>
              <ResourceIcon k={k} />
              <span className="num">{formatAmount(amount)}</span>
              {trend && <span className={`trend ${trend}`} aria-hidden="true">{b > 0 ? '\u25B2' : '\u25BC'}</span>}
              {i === 0 && <IconChevron className="chev" />}
            </button>
          );
        })}
        {h && (
          <button type="button" className="hud-chip extra" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-label={`${t('house.prestige')}: ${Math.floor(h.house.prestige)}. ${t('ui.resources')}`} title={t('house.prestige')}>
            <ResPrestige /><span className="num">{formatAmount(h.house.prestige)}</span>
          </button>
        )}
      </div>
      <div className="hud-actions">
        <span className={`ws-dot ${wsConnected ? 'on' : ''}`} role="img" aria-label={wsConnected ? t('hud.live') : t('hud.offline')} title={wsConnected ? t('hud.live') : t('hud.offline')} />
        <NavLink to="/search" className={link} aria-label={t('nav.search')}><IconSearch /></NavLink>
        <NavLink to="/chronicle" className={(x) => `${link(x)} optional`} aria-label={t('nav.chronicle')}><IconBook /></NavLink>
        <NavLink to="/settings" className={link} aria-label={t('nav.settings')}><IconGear /></NavLink>
      </div>
      <Sheet open={open} onClose={() => setOpen(false)} title={t('house.treasury')}>
        <div className="res-sheet">
          {h && <ResourceChips resources={h.house.resources} balance={h.monthlyBalance} />}
          <p className="small muted" style={{ marginTop: 12 }}>{t('house.balanceNote')}</p>
          {w && (
            <p className="hud-sheet-next small">
              <IconHourglass /><span>{t('house.nextDay')}</span>
              <Countdown key={w.nextDayAtMs} targetMs={w.nextDayAtMs} />
            </p>
          )}
          {h && (
            <div className="hud-sheet-links">
              <Link to={`/tree/${h.house.id}`} className="btn small quiet" onClick={() => setOpen(false)}><ResPrestige />{t('house.prestige')}: {Math.floor(h.house.prestige)}</Link>
              <Link to="/chronicle" className="btn small quiet" onClick={() => setOpen(false)}><IconBook />{t('nav.chronicle')}</Link>
            </div>
          )}
        </div>
      </Sheet>
    </header>
  );
}

function Toasts() {
  const toasts = useUi((s) => s.toasts);
  const dismiss = useUi((s) => s.dismissToast);
  return (
    <div className="toasts" aria-live="polite">
      {toasts.map((x) => (
        <div key={x.id} className={`toast ${x.kind}`} onClick={() => dismiss(x.id)}>{x.text}</div>
      ))}
    </div>
  );
}
